import type {
  DiaSemana,
  HorarioDoDiaType,
  SituacaoAluno,
  SituacaoMatricula,
  TipoAtendimento,
} from './enums';
import { DURACAO_SLOT_MIN, minutosDoHorario } from '../horario';
import { slotsOcupados } from './ocupacao';

/**
 * Snapshot bruto da unidade: `GET /painel` não agrega nada; agenda e painel são derivados dele no client.
 * `horarios` só traz os vigentes (`desativadoEm: null`). Já vem escopado por professor na query
 * (`escopoProfessorDoUsuario()`); o `professorId` dos helpers abaixo é um filtro adicional (ex.: pool de uma matéria).
 */
export type PainelDadosOutput = {
  professores: {
    id: string;
    nome: string;
    diasDisponiveis: DiaSemana[];
    horarioInicial: HorarioDoDiaType;
    horarioFinal: HorarioDoDiaType;
    capacidadePorHorario: number;
    materiaIds: string[];
    corAgenda: string;
  }[];
  alunos: {
    id: string;
    nome: string;
    situacao: SituacaoAluno;
    zonaVermelha: boolean;
    connect: boolean;
  }[];
  materias: {
    id: string;
    nome: string;
    conteudos: {
      id: string;
      nome: string;
    }[];
  }[];
  matriculas: {
    id: string;
    alunoId: string;
    professorId: string;
    materiaId: string;
    estagio: string | null;
    situacao: SituacaoMatricula;
    tipoAtendimento: TipoAtendimento;
    horarios: {
      id: string;
      diaSemana: DiaSemana;
      horario: HorarioDoDiaType;
    }[];
  }[];
};
export type PainelDadosOutputType = PainelDadosOutput;

// Sem 'DOM': nenhuma unidade Kumon abre aos domingos.
const DIAS_BANCO = ['SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SAB'] as const;

/** Vagas-horario que um professor abre por semana -- base de `ocupacaoPercentual`. */
function capacidadeSemanal(professor: {
  diasDisponiveis: string[];
  horarioInicial: string;
  horarioFinal: string;
  capacidadePorHorario: number;
}): number {
  const inicio = minutosDoHorario(professor.horarioInicial);
  const fim = minutosDoHorario(professor.horarioFinal);
  const slotsPorDia = Math.max(0, Math.floor((fim - inicio) / DURACAO_SLOT_MIN));
  return professor.diasDisponiveis.length * slotsPorDia * professor.capacidadePorHorario;
}

export interface PainelAgregacoes {
  totalAlunosAtivos: number;
  totalMatriculasAtivas: number;
  totalProfessores: number;
  ocupacaoPercentual: number;
  /** Soma de `capacidadePorHorario`: teto de alunos simultâneos na unidade (Kumon não é 1:1). Alimenta o card "Capacidade". */
  capacidadeSimultanea: number;
  matriculasPorMateria: { materiaId: string; materiaNome: string; total: number }[];
  matriculasPorProfessor: { professorId: string; professorNome: string; total: number }[];
  aulasPorDiaSemana: { diaSemana: string; total: number }[];
  alertas: { tipo: string; alunoId: string; mensagem: string }[];
}

/** Calculadas sobre o snapshot (que já pode vir cortado por escopo); `professorId` recorta um pool dentro dele. `totalProfessores` nunca é filtrado. */
export function calcularAgregacoesPainel(
  dados: PainelDadosOutputType,
  professorId: string | string[] | null = null,
): PainelAgregacoes {
  const professorIds =
    professorId === null ? null : Array.isArray(professorId) ? professorId : [professorId];

  const matriculasEscopadas = professorIds
    ? dados.matriculas.filter((matricula) => professorIds.includes(matricula.professorId))
    : dados.matriculas;

  const alunoIdsEscopados = new Set(matriculasEscopadas.map((matricula) => matricula.alunoId));
  const alunosEscopados = professorIds
    ? dados.alunos.filter((aluno) => alunoIdsEscopados.has(aluno.id))
    : dados.alunos;

  const matriculasAtivas = matriculasEscopadas.filter(
    (matricula) => matricula.situacao === 'ATIVA',
  );

  const professoresParaCapacidade = professorIds
    ? dados.professores.filter((professor) => professorIds.includes(professor.id))
    : dados.professores;
  const capacidadeTeorica = professoresParaCapacidade.reduce(
    (soma, professor) => soma + capacidadeSemanal(professor),
    0,
  );
  // Comparável com `totalAlunosAtivos`; `capacidadeTeorica` multiplica por dias x slots e dá ordem de grandeza maior.
  const capacidadeSimultanea = professoresParaCapacidade.reduce(
    (soma, professor) => soma + professor.capacidadePorHorario,
    0,
  );

  const horariosEscopados = matriculasEscopadas.flatMap((matricula) =>
    matricula.horarios.map((horario) => ({
      ...horario,
      tipoAtendimento: matricula.tipoAtendimento,
    })),
  );
  const totalSlotsOcupados = horariosEscopados.reduce(
    (soma, horario) => soma + slotsOcupados(horario.tipoAtendimento),
    0,
  );
  const ocupacaoPercentual =
    capacidadeTeorica > 0 ? Math.round((totalSlotsOcupados / capacidadeTeorica) * 1000) / 10 : 0;

  const nomeDaMateria = new Map(dados.materias.map((materia) => [materia.id, materia.nome]));
  const porMateria = new Map<string, number>();
  for (const matricula of matriculasAtivas) {
    porMateria.set(matricula.materiaId, (porMateria.get(matricula.materiaId) ?? 0) + 1);
  }
  const matriculasPorMateria = [...porMateria.entries()].map(([materiaId, total]) => ({
    materiaId,
    materiaNome: nomeDaMateria.get(materiaId) ?? '',
    total,
  }));

  const nomeDoProfessor = new Map(
    dados.professores.map((professor) => [professor.id, professor.nome]),
  );
  const porProfessor = new Map<string, number>();
  for (const matricula of matriculasAtivas) {
    porProfessor.set(matricula.professorId, (porProfessor.get(matricula.professorId) ?? 0) + 1);
  }
  const matriculasPorProfessor = [...porProfessor.entries()].map(([professorId, total]) => ({
    professorId,
    professorNome: nomeDoProfessor.get(professorId) ?? '',
    total,
  }));

  const porDia = new Map<string, number>();
  for (const horario of horariosEscopados) {
    porDia.set(horario.diaSemana, (porDia.get(horario.diaSemana) ?? 0) + 1);
  }
  const aulasPorDiaSemana = DIAS_BANCO.map((dia) => ({
    diaSemana: dia,
    total: porDia.get(dia) ?? 0,
  }));

  const alertas = alunosEscopados
    .filter((aluno) => aluno.situacao === 'ATIVO' && aluno.zonaVermelha)
    .sort((a, b) => a.nome.localeCompare(b.nome))
    .map((aluno) => ({
      tipo: 'zona_vermelha',
      alunoId: aluno.id,
      // Formulação neutra de propósito: "está marcado" não concorda com o gênero do nome.
      mensagem: `${aluno.nome} está na zona vermelha.`,
    }));

  return {
    totalAlunosAtivos: alunosEscopados.filter((aluno) => aluno.situacao === 'ATIVO').length,
    totalMatriculasAtivas: matriculasAtivas.length,
    totalProfessores: dados.professores.length,
    ocupacaoPercentual,
    capacidadeSimultanea,
    matriculasPorMateria,
    matriculasPorProfessor,
    aulasPorDiaSemana,
    alertas,
  };
}
